import React, { useState, useEffect } from 'react';
import { Home, TrendingUp, AlertCircle, CheckCircle, MessageSquare, Calendar } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';


const StatisticsCards = ({ statistics }) => {
  const [requestStats, setRequestStats] = useState({
    totalInquiries: 0,
    newInquiries: 0,
    inProgressInquiries: 0,
    respondedInquiries: 0
  });
  const [appointmentStats, setAppointmentStats] = useState({
    totalAppointments: 0,
    scheduledAppointments: 0,
    confirmedAppointments: 0,
    completedAppointments: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDetailedStats();
  }, []);

  const fetchDetailedStats = async () => {
    setLoading(true);
    try {
      // Fetch property inquiries statistics
      const { data: inquiries } = await supabase?.from('property_inquiries')
        ?.select('status')
        ?.order('created_at', { ascending: false });

      const allInquiries = inquiries || [];
      
      // Fetch appointments statistics
      const { data: appointments } = await supabase?.from('appointments')
        ?.select('status')
        ?.order('created_at', { ascending: false });

      const allAppointments = appointments || [];

      // Calculate request statistics
      const requestStatsData = {
        totalInquiries: allInquiries?.length,
        newInquiries: allInquiries?.filter(i => i?.status === 'new')?.length,
        inProgressInquiries: allInquiries?.filter(i => i?.status === 'in_progress')?.length,
        respondedInquiries: allInquiries?.filter(i => i?.status === 'responded')?.length
      };

      // Calculate appointment statistics
      const appointmentStatsData = {
        totalAppointments: allAppointments?.length,
        scheduledAppointments: allAppointments?.filter(a => a?.status === 'scheduled')?.length,
        confirmedAppointments: allAppointments?.filter(a => a?.status === 'confirmed')?.length,
        completedAppointments: allAppointments?.filter(a => a?.status === 'completed')?.length
      };

      setRequestStats(requestStatsData);
      setAppointmentStats(appointmentStatsData);
      
      console.log('📊 Detailed statistics loaded:', { requestStatsData, appointmentStatsData });
    } catch (error) {
      console.error('Error fetching detailed statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount || 0);
  };

  const statCards = [
    {
      title: 'Propriétés Totales',
      value: statistics?.totalListings || 0,
      icon: Home,
      color: 'bg-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      change: `${statistics?.activeProperties || 0} actives`,
      changeType: 'neutral'
    },
    {
      title: 'Demandes Clients',
      value: requestStats?.totalInquiries,
      icon: MessageSquare,
      color: 'bg-orange-500',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600',
      change: `${requestStats?.newInquiries} nouvelles`,
      changeType: requestStats?.newInquiries > 0 ? 'positive' : 'neutral'
    },
    {
      title: 'Rendez-vous',
      value: appointmentStats?.totalAppointments,
      icon: Calendar,
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      change: `${appointmentStats?.confirmedAppointments} confirmés`,
      changeType: 'neutral'
    },
    {
      title: 'Propriétés Vendues',
      value: statistics?.soldProperties || 0,
      icon: CheckCircle,
      color: 'bg-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      change: '+8% ce mois',
      changeType: 'positive'
    },
    {
      title: 'En Attente',
      value: statistics?.pendingApprovals || 0,
      icon: AlertCircle,
      color: 'bg-yellow-500',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-600',
      change: 'À traiter',
      changeType: statistics?.pendingApprovals > 0 ? 'warning' : 'neutral'
    },
    {
      title: 'Revenus Générés',
      value: formatCurrency(statistics?.totalRevenue),
      icon: TrendingUp,
      color: 'bg-indigo-500',
      bgColor: 'bg-indigo-50',
      textColor: 'text-indigo-600',
      change: '+12%',
      changeType: 'positive'
    }
  ];

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        {[...Array(6)]?.map((_, index) => (
          <div key={index} className="bg-white rounded-lg shadow-sm p-6">
            <div className="animate-pulse">
              <div className="flex items-center justify-between mb-4">
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-12 w-12 bg-gray-200 rounded-lg"></div>
              </div>
              <div className="h-8 bg-gray-200 rounded w-1/3 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
      {statCards?.map((card, index) => {
        const Icon = card?.icon;
        return (
          <div key={index} className="bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">{card?.title}</p>
                <p className="text-2xl font-bold text-gray-900">
                  {typeof card?.value === 'string' ? card?.value : (card?.value || 0)?.toLocaleString()}
                </p>
              </div>
              <div className={`${card?.bgColor} p-3 rounded-lg`}>
                <Icon className={`h-6 w-6 ${card?.textColor}`} />
              </div>
            </div>
            <div className="flex items-center">
              <span className={`text-sm font-medium ${
                card?.changeType === 'positive' ? 'text-green-600' : 
                card?.changeType === 'warning' ? 'text-yellow-600' :
                card?.changeType === 'negative' ? 'text-red-600' : 'text-gray-600'
              }`}>
                {card?.change}
              </span>
            </div>
          </div>
        );
      })}
      {/* Additional Detail Cards for Requests */}
      <div className="md:col-span-2 lg:col-span-3">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Aperçu des Demandes Clients
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <p className="text-2xl font-bold text-orange-600">{requestStats?.newInquiries}</p>
              <p className="text-sm text-orange-700">Nouvelles</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <p className="text-2xl font-bold text-yellow-600">{requestStats?.inProgressInquiries}</p>
              <p className="text-sm text-yellow-700">En cours</p>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-2xl font-bold text-green-600">{requestStats?.respondedInquiries}</p>
              <p className="text-sm text-green-700">Traitées</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{requestStats?.totalInquiries}</p>
              <p className="text-sm text-blue-700">Total</p>
            </div>
          </div>
          
          {requestStats?.totalInquiries === 0 && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-gray-400" />
                <p className="text-sm text-gray-600">
                  Aucune demande client pour le moment. Les demandes apparaîtront automatiquement 
                  lorsque les clients utilisent les formulaires sur les pages de propriétés.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StatisticsCards;